/* eslint-disable react/no-unstable-nested-components */
import { useMutation, useQuery } from "@apollo/client";
import compareAsc from "date-fns/compareAsc";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import mapValues from "lodash/mapValues";
import flatten from "lodash/flatten";
import { invariant } from "outvariant";
import getCategoryOrder from "components/financials/getCategoryOrder";
import FinancialsAvailabilityQuery from "components/financials/modern/graphql/FinancialsAvailabilityQuery";
import FinancialsSheetQuery from "components/financials/modern/graphql/FinancialsSheetQuery";
import IndicatorsQuery from "components/financials/modern/graphql/IndicatorsQuery";
import type {
  IFinancialsAvailabilityQuery,
  IFinancialsAvailabilityQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsAvailabilityQuery.generated";
import type {
  IFinancialsSheetQuery,
  IFinancialsSheetQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import type {
  IIndicatorsQuery,
  IIndicatorsQueryVariables,
} from "components/financials/modern/graphql/__generated__/IndicatorsQuery.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import sortArray from "lib/utils/sortArray";
import type { IIndicatorDataSource, IIndicatorsFilters, ISecurityCountry, IPlatformVertical } from "types/graphqlTypes";
import {
  IFinancialsSheetIndicatorsCriterion,
  IFinancialsSheetLimitCriterion,
  IFinancialsStatementType,
  IIndicatorCategory,
  IIndicatorsTablePlacement,
} from "types/graphqlTypes";
import type {
  IExportFinancialsSheetMutation,
  IExportFinancialsSheetMutationVariables,
} from "components/financials/modern/graphql/__generated__/ExportFinancialsSheetMutation.generated";
import ExportFinancialsSheetMutation from "components/financials/modern/graphql/ExportFinancialsSheetMutation";
import { until } from "@open-draft/until";
import downloadFileUrl from "lib/utils/downloadFileUrl";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useIntl } from "react-intl";

export function getSortedIndicatorCategories(indicators: Pick<IIndicatorFieldsFragment, "category">[]) {
  const byCategories: Record<IIndicatorCategory, IIndicatorFieldsFragment[]> = {
    [IIndicatorCategory.BalanceSheet]: [],
    [IIndicatorCategory.IncomeStatement]: [],
    [IIndicatorCategory.CashFlow]: [],
    [IIndicatorCategory.Payout]: [],
    [IIndicatorCategory.Ratios]: [],
    [IIndicatorCategory.Metrics]: [],
    [IIndicatorCategory.Fundamentei]: [],
    [IIndicatorCategory.Other]: [],
    [IIndicatorCategory.Valuation]: [],
    [IIndicatorCategory.Compensation]: [],
    ...groupBy(indicators, "category"),
  };
  const categories = [...new Set(indicators.map((indicator) => indicator.category))].map((category) => ({
    category,
    totalCount: byCategories[category].length,
  }));

  return categories.sort((a, b) => getCategoryOrder(a.category) - getCategoryOrder(b.category));
}

export function useIndicators(filters: IIndicatorsFilters | null = null) {
  const { loading, data } = useQuery<IIndicatorsQuery, IIndicatorsQueryVariables>(IndicatorsQuery, {
    variables: {
      objectIds: null,
      filters,
    },
  });
  const indicators = data?.indicators || [];
  const byCategories: Record<IIndicatorCategory, IIndicatorFieldsFragment[]> = {
    [IIndicatorCategory.BalanceSheet]: [],
    [IIndicatorCategory.IncomeStatement]: [],
    [IIndicatorCategory.CashFlow]: [],
    [IIndicatorCategory.Payout]: [],
    [IIndicatorCategory.Ratios]: [],
    [IIndicatorCategory.Metrics]: [],
    [IIndicatorCategory.Fundamentei]: [],
    [IIndicatorCategory.Other]: [],
    [IIndicatorCategory.Valuation]: [],
    [IIndicatorCategory.Compensation]: [],
    ...groupBy(indicators, "category"),
  };
  const categories = [...new Set(indicators.map((indicator) => indicator.category))].map((category) => ({
    category,
    totalCount: byCategories[category].length,
  }));

  const sortedCategories = categories.sort((a, b) => getCategoryOrder(a.category) - getCategoryOrder(b.category));
  return {
    loading,
    indicators,
    categories,
    sortedCategories,
    byCategories,
  };
}

type UseFinancialsChartParams = {
  tickerSymbol: string;
  country: ISecurityCountry;
  statementType: IFinancialsStatementType;
  currency: string;
  filterByInterim?: string | null;
  limit?: number;
  limitCriterion?: IFinancialsSheetLimitCriterion;
  arbitraryIndicators?: Pick<IIndicatorFieldsFragment, "objectId">[];
  indicatorsCriterion?: IFinancialsSheetIndicatorsCriterion;
  interim: string | null;
  vertical: IPlatformVertical;
  indicatorDataSource: IIndicatorDataSource;
  templateId?: string | null;
  skip?: boolean;
};

export function useFinancialsChart({
  tickerSymbol,
  country,
  statementType,
  currency,
  arbitraryIndicators = [],
  indicatorsCriterion = IFinancialsSheetIndicatorsCriterion.Only,
  interim,
  vertical,
  indicatorDataSource,
  templateId,
  skip = false,
  limitCriterion = IFinancialsSheetLimitCriterion.Unbounded,
  limit = 240,
}: UseFinancialsChartParams) {
  const { data, previousData, loading, refetch, networkStatus } = useQuery<
    IFinancialsSheetQuery,
    IFinancialsSheetQueryVariables
  >(FinancialsSheetQuery, {
    skip,
    notifyOnNetworkStatusChange: true,
    variables: {
      filterByReportDateAfter: null,
      filterByReportDateBefore: null,
      indicatorsCriterion,
      limitCriterion,
      indicators: arbitraryIndicators.map(({ objectId }) => objectId),
      filterByInterim: interim,
      statementType,
      tickerSymbol,
      country,
      currency,
      limit,
      financialsTemplate: templateId,
      filterByCountryCode: null,
      vertical,
      indicatorDataSource,
    },
  });

  const financialsSheet = (loading && !data ? previousData?.financialsSheet : data?.financialsSheet) || null;
  const rows = financialsSheet
    ? financialsSheet.reportDates.map((reportDate, index) => {
        guardAgainstCorruptedData(financialsSheet);

        return {
          index,

          reportDate: parseISO(reportDate),
          interim: financialsSheet.interim[index],
          forexRate: financialsSheet.forexRates[index],
          context: financialsSheet.context[index],

          isTrailingTwelveMonths: financialsSheet.isTrailingTwelveMonths[index] || false,
          isPreliminaryTrailingTwelveMonths: financialsSheet.isPreliminaryTrailingTwelveMonths[index] || false,
          isQuarterly: financialsSheet.isQuarterly[index] || false,
          isAnnual: financialsSheet.isAnnual[index] || false,
        };
      })
    : [];

  const indicators = (financialsSheet ? financialsSheet.indicators : []).map((indicator) => ({
    ...indicator,

    reportDates: rows.map((row) => row.reportDate),
    interim: rows.map((row) => row.interim),
    forexRates: rows.map((row) => row.forexRate),

    isTrailingTwelveMonths: rows.map((row) => row.isTrailingTwelveMonths),
    isPreliminaryTrailingTwelveMonths: rows.map((row) => row.isPreliminaryTrailingTwelveMonths),
    isQuarterly: rows.map((row) => row.isQuarterly),
    isAnnual: rows.map((row) => row.isAnnual),

    // Series data
    series: rows.map((row) => indicator.series[row.index]),
  }));

  const template =
    financialsSheet?.viewerSelectedTemplate || financialsSheet?.overridingTemplate || financialsSheet?.systemTemplate;

  return {
    networkStatus,
    loading,
    refetch,

    rows,
    indicators,
    financialsTemplate: template || null,
    viewerSelectedTemplate: financialsSheet?.viewerSelectedTemplate || null,
    financialsSheet,
  };
}

type UseFinancialsSheetParams = {
  tickerSymbol: string;
  country: ISecurityCountry;
  statementType: IFinancialsStatementType;
  currency: string;
  financialsTemplate: string | null;
  filterByReportDates: [Date, Date] | null;
  filterByInterim: string | null;
  vertical: IPlatformVertical;
  indicatorDataSource: IIndicatorDataSource;
  lastYears?: number;
  limit?: number;
  indicatorsCriterion?: IFinancialsSheetIndicatorsCriterion;
  indicatorsPlacement?: IIndicatorsTablePlacement;
  arbitraryIndicators?: Pick<IIndicatorFieldsFragment, "objectId">[];
  sortDirection?: "ASC" | "DESC";
};

export function useFinancialsSheet({
  tickerSymbol,
  country,
  statementType,
  currency,
  financialsTemplate,
  filterByReportDates,
  filterByInterim,
  vertical,
  indicatorDataSource,
  lastYears,
  arbitraryIndicators = [],
  indicatorsCriterion = IFinancialsSheetIndicatorsCriterion.Include,
  indicatorsPlacement = IIndicatorsTablePlacement.End,
  limit = 30,
  sortDirection = "ASC",
}: UseFinancialsSheetParams) {
  const intl = useIntl();
  const variables = {
    filterByCountryCode: null,
    limit,
    limitCriterion: IFinancialsSheetLimitCriterion.NumberOfRows,
    ...getFilterByReportDatesVariables(statementType, filterByReportDates, lastYears),
    indicatorsCriterion,
    indicatorsPlacement,
    indicators: arbitraryIndicators.map(({ objectId }) => objectId),
    statementType,
    tickerSymbol,
    country,
    currency,
    financialsTemplate,
    filterByInterim,
    vertical,
    indicatorDataSource,
  };
  const { data, previousData, loading, refetch, networkStatus } = useQuery<
    IFinancialsSheetQuery,
    IFinancialsSheetQueryVariables
  >(FinancialsSheetQuery, {
    notifyOnNetworkStatusChange: true,
    variables,
  });

  const [exportFinancialsSheet] = useMutation<IExportFinancialsSheetMutation, IExportFinancialsSheetMutationVariables>(
    ExportFinancialsSheetMutation,
  );

  const financialsSheet = (loading && !data ? previousData?.financialsSheet : data?.financialsSheet) || null;
  const _rows = financialsSheet
    ? financialsSheet.reportDates.map((reportDate, index) => {
        guardAgainstCorruptedData(financialsSheet);

        return {
          index,

          reportDate: parseISO(reportDate),
          interim: financialsSheet.interim[index],
          forexRate: financialsSheet.forexRates[index],
          context: financialsSheet.context[index],

          isTrailingTwelveMonths: financialsSheet.isTrailingTwelveMonths[index] || false,
          isPreliminaryTrailingTwelveMonths: financialsSheet.isPreliminaryTrailingTwelveMonths[index] || false,
          isQuarterly: financialsSheet.isQuarterly[index] || false,
          isAnnual: financialsSheet.isAnnual[index] || false,
        };
      })
    : [];

  const rows = sortArray(_rows, "DESC", sortDirection);
  const indicators = (financialsSheet ? financialsSheet.indicators : []).map((indicator) => ({
    ...indicator,

    reportDates: sortArray(
      rows.map((row) => row.reportDate),
      "DESC",
      sortDirection,
    ),
    interim: sortArray(
      rows.map((row) => row.interim),
      "DESC",
      sortDirection,
    ),
    forexRates: sortArray(
      rows.map((row) => row.forexRate),
      "DESC",
      sortDirection,
    ),

    isTrailingTwelveMonths: sortArray(
      rows.map((row) => row.isTrailingTwelveMonths),
      "DESC",
      sortDirection,
    ),
    isPreliminaryTrailingTwelveMonths: sortArray(
      rows.map((row) => row.isPreliminaryTrailingTwelveMonths),
      "DESC",
      sortDirection,
    ),
    isQuarterly: sortArray(
      rows.map((row) => row.isQuarterly),
      "DESC",
      sortDirection,
    ),
    isAnnual: sortArray(
      rows.map((row) => row.isAnnual),
      "DESC",
      sortDirection,
    ),
    context: sortArray(
      rows.map((row) => row.context),
      "DESC",
      sortDirection,
    ),

    // Series data
    series: sortArray(
      rows.map((row) => indicator.series[row.index]),
      "DESC",
      sortDirection,
    ),
  }));

  const performExportFinancialsSheet = useCallback(
    async (format: "csv" | "xlsx") => {
      const { data } = await toast.promise(
        until(async () => {
          return exportFinancialsSheet({
            variables: {
              format,
              ...variables,
            },
          });
        }),
        {
          success: intl.formatMessage({ defaultMessage: "Exportado com sucesso", id: "ikJYsb" }),
          error: intl.formatMessage({ defaultMessage: "Erro ao exportar", id: "d68IeF" }),
          loading: intl.formatMessage({ defaultMessage: "Exportando...", id: "p41wgZ" }),
        },
      );
      if (data?.data?.exportFinancialsSheet) {
        downloadFileUrl(data.data.exportFinancialsSheet);
      }
    },
    [variables],
  );

  return {
    networkStatus,
    loading,
    refetch,
    exportFinancialsSheet: performExportFinancialsSheet,
    variables,

    rows,
    indicators,
    financialsTemplate: financialsSheet?.systemTemplate || null,
    financialsSheet,
    systemTemplate: financialsSheet?.systemTemplate || null,
    overridingTemplate: financialsSheet?.overridingTemplate || null,
    renderingTemplate:
      financialsSheet?.viewerSelectedTemplate ||
      financialsSheet?.overridingTemplate ||
      financialsSheet?.systemTemplate ||
      null,
  };
}

function guardAgainstCorruptedData(financialsSheet: IFinancialsSheetQuery["financialsSheet"]) {
  invariant(financialsSheet !== null, "Corrupted sheet data");
  invariant(financialsSheet.reportDates.length === financialsSheet.interim.length, "Corrupted sheet data");
  invariant(
    financialsSheet.reportDates.length === financialsSheet.isTrailingTwelveMonths.length,
    "Corrupted sheet data",
  );
  invariant(financialsSheet.reportDates.length === financialsSheet.isQuarterly.length, "Corrupted sheet data");
  invariant(financialsSheet.reportDates.length === financialsSheet.isAnnual.length, "Corrupted sheet data");
  invariant(financialsSheet.reportDates.length === financialsSheet.forexRates.length, "Corrupted sheet data");
}

export function useFinancialsAvailability(
  tickerSymbol: string,
  country: ISecurityCountry,
  vertical: IPlatformVertical,
  indicatorDataSource: IIndicatorDataSource,
) {
  const { data, loading } = useQuery<IFinancialsAvailabilityQuery, IFinancialsAvailabilityQueryVariables>(
    FinancialsAvailabilityQuery,
    {
      variables: {
        tickerSymbol,
        country,
        vertical,
        indicatorDataSource,
      },
    },
  );

  const safe = data?.financialsAvailability || [];

  const reportsDatesByStatementType: Record<IFinancialsStatementType, Date[]> = {
    [IFinancialsStatementType.Annual]: [],
    [IFinancialsStatementType.Monthly]: [],
    [IFinancialsStatementType.Quarterly]: [],
    ...mapValues(keyBy(safe, "statementType"), ({ reportDates }) => {
      return reportDates.map((reportDate) => parseISO(reportDate)).sort(compareAsc);
    }),
  };

  const allReportDates = flatten(safe.map((node) => node.reportDates))
    .map((reportDate) => parseISO(reportDate))
    .sort(compareAsc);

  return {
    loading,
    allReportDates,
    reportsDatesByStatementType,
  };
}

function getFilterByReportDatesVariables(
  statementType: IFinancialsStatementType,
  filterByReportDates: [Date, Date] | null,
  limit?: number,
): Pick<IFinancialsSheetQueryVariables, "filterByReportDateAfter" | "filterByReportDateBefore" | "limit"> {
  if (!filterByReportDates) {
    // return {
    //   filterByReportDateAfter: lightFormat(startOfYear(subYears(new Date(), limit)), "yyyy-MM-dd"),
    //   filterByReportDateBefore: lightFormat(endOfMonth(new Date()), "yyyy-MM-dd"),
    // };
    const defaultLimit = statementType === IFinancialsStatementType.Quarterly ? 3 : 10;
    return {
      limit: limit || defaultLimit,
      filterByReportDateAfter: null,
      filterByReportDateBefore: null,
    };
  }
  return {
    filterByReportDateAfter: lightFormat(filterByReportDates[0], "yyyy-MM-dd"),
    filterByReportDateBefore: lightFormat(filterByReportDates[1], "yyyy-MM-dd"),
  };
}
